import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShoppingBag, TrendingUp, Users, Mail, Star, Package,
  MessageSquare, Send, ArrowRight, DollarSign, Image as ImageIcon,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { useCurrency } from "../context/CurrencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { formatPrice } = useCurrency();
  const [kpis, setKpis] = useState(null);
  const [top, setTop] = useState([]);
  const [series, setSeries] = useState([]);
  const [cohorts, setCohorts] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, t, s, c, w] = await Promise.all([
          axios.get(`${API}/admin/analytics/overview`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics/top-products`, { params: { limit: 5 }, withCredentials: true }),
          axios.get(`${API}/admin/analytics/revenue-series`, { params: { days: 30 }, withCredentials: true }),
          axios.get(`${API}/admin/analytics/cohorts`, { params: { months: 6 }, withCredentials: true }),
          axios.get(`${API}/admin/whoami`, { withCredentials: true }),
        ]);
        setKpis(o.data); setTop(t.data); setSeries(s.data); setCohorts(c.data); setMe(w.data);
      } catch (e) {
        // Route guard in AdminLayout will redirect on 401 — nothing to display here
        if (e?.response && e.response.status !== 401) console.debug("[AdminDashboard] whoami:", e.message);
      }
    })();
  }, []);

  return (
    <AdminLayout current="dashboard">
      <div data-testid="admin-dashboard">
        <div className="mb-12">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">Tableau de bord</p>
          <h1 className="font-serif text-4xl md:text-5xl">
            Bonjour, <em className="text-[color:var(--gold)]">{me?.full_name?.split(" ")[0] || "Administrateur"}</em>.
          </h1>
        </div>

        {/* KPI cards */}
        {kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <KPI title="Ventes aujourd'hui" value={formatPrice(kpis.today.revenue)} sub={`${kpis.today.count} commandes`} icon={DollarSign} testid="kpi-today" />
            <KPI title="7 derniers jours" value={formatPrice(kpis.week.revenue)} sub={`${kpis.week.count} commandes`} icon={TrendingUp} testid="kpi-week" />
            <KPI title="30 derniers jours" value={formatPrice(kpis.month.revenue)} sub={`${kpis.month.count} commandes`} icon={ShoppingBag} testid="kpi-month" />
            <KPI title="Panier moyen" value={formatPrice(kpis.aov)} sub={`sur ${kpis.total_paid_orders} paiements`} icon={ShoppingBag} testid="kpi-aov" />
            <KPI title="Taux conversion" value={`${kpis.conversion_rate}%`} sub={`30 jours`} icon={TrendingUp} testid="kpi-conversion" gold />
            <KPI title="Clients" value={kpis.customers.total} sub={`+${kpis.customers.new_30d} ce mois`} icon={Users} testid="kpi-customers" />
            <KPI title="Newsletter" value={kpis.newsletter} sub="inscrits" icon={Mail} testid="kpi-newsletter" />
            <KPI title="Paniers abandonnés" value={kpis.abandoned_carts} sub="7 jours" icon={Package} testid="kpi-abandoned" alert={kpis.abandoned_carts > 0} />
          </div>
        )}

        {/* Revenue chart + Top products */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 border border-white/10 p-6" data-testid="revenue-chart">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Chiffre d'affaires</p>
                <p className="font-serif text-2xl mt-1">30 derniers jours</p>
              </div>
              <p className="label-caps text-[10px] text-[color:var(--gold)] tracking-[0.25em]">EUR</p>
            </div>
            <RevenueSparkline series={series} />
          </div>

          <div className="border border-white/10 p-6" data-testid="top-products">
            <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Top produits</p>
            <p className="font-serif text-2xl mt-1 mb-6">Meilleures ventes</p>
            {top.length === 0 && <p className="text-white/40 text-sm">Aucune vente encore.</p>}
            <div className="space-y-3">
              {top.map((p, i) => (
                <div key={p._id} className="flex items-center justify-between border-b border-white/5 pb-3">
                  <div className="min-w-0">
                    <p className="text-white/50 text-[10px] label-caps tracking-[0.2em]">#{i + 1}</p>
                    <p className="text-white truncate">{p.name}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-[color:var(--gold)] font-serif">{formatPrice(p.revenue)}</p>
                    <p className="text-white/40 text-xs">{p.qty} vendus</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cohort retention matrix */}
        {cohorts && cohorts.cohorts && cohorts.cohorts.length > 0 && (
          <div className="border border-white/10 p-6 mb-10" data-testid="cohorts-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">Cohortes clients</p>
                <p className="font-serif text-2xl mt-1">Taux de rétention par mois</p>
              </div>
              <p className="text-white/40 text-xs">6 derniers mois</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 label-caps text-[10px] tracking-[0.2em]">
                    <th className="text-left py-2 pr-4">Cohorte</th>
                    <th className="text-right py-2 pr-3">Clients</th>
                    {Array.from({ length: cohorts.months }).map((_, i) => (
                      <th key={i} className="text-center py-2 px-2">M+{i}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.cohorts.map((row) => (
                    <tr key={row.cohort} className="border-t border-white/5">
                      <td className="py-3 pr-4 font-serif text-white">{row.cohort}</td>
                      <td className="py-3 pr-3 text-right text-white/70">{row.size}</td>
                      {row.retention.map((cell, i) => (
                        <td key={i} className="py-3 px-1 text-center">
                          {cell === null ? (
                            <span className="text-white/20 text-xs">—</span>
                          ) : (
                            <span
                              className="inline-block min-w-[52px] py-1 rounded-sm"
                              style={{
                                background: cell.rate > 0
                                  ? `rgba(212,175,55,${Math.min(0.05 + (cell.rate / 100) * 0.55, 0.6)})`
                                  : "transparent",
                                color: cell.rate > 0 ? "#fff" : "rgba(255,255,255,0.3)",
                              }}
                              title={`${cell.count}/${row.size} clients actifs en ${cell.month}`}
                            >
                              {cell.rate}%
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-white/40 text-xs mt-4">
              Chaque cellule = % de la cohorte ayant passé au moins 1 commande payée dans le mois.
            </p>
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction to="/admin/orders" icon={Package} label="Commandes" desc="Gérer expéditions & statuts" testid="qa-orders" />
          <QuickAction to="/admin/library" icon={ImageIcon} label="Médiathèque" desc="Gérer photos & vidéos" testid="qa-library" />
          <QuickAction to="/admin/reviews" icon={Star} label={`Avis${kpis?.pending_reviews > 0 ? ` (${kpis.pending_reviews})` : ""}`} desc="Modération achats vérifiés" testid="qa-reviews" alert={kpis?.pending_reviews > 0} />
          <QuickAction to="/admin/messages" icon={MessageSquare} label={`Messages${kpis?.unread_messages > 0 ? ` (${kpis.unread_messages})` : ""}`} desc="Contacts clients" testid="qa-messages" alert={kpis?.unread_messages > 0} />
        </div>
      </div>
    </AdminLayout>
  );
}

const KPI = ({ title, value, sub, icon: Icon, testid, gold, alert }) => (
  <div className={`border p-5 ${gold ? "border-[color:var(--gold)]" : alert ? "border-orange-400/40" : "border-white/10"} transition-colors`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">{title}</p>
      <Icon size={16} className={gold ? "text-[color:var(--gold)]" : "text-white/30"} strokeWidth={1.5} />
    </div>
    <p className={`font-serif text-3xl ${gold ? "text-[color:var(--gold)]" : "text-white"}`}>{value}</p>
    <p className="text-white/40 text-xs mt-1">{sub}</p>
  </div>
);

const QuickAction = ({ to, icon: Icon, label, desc, testid, alert }) => (
  <Link to={to} data-testid={testid}
    className={`border ${alert ? "border-orange-400/40 hover:border-orange-400" : "border-white/10 hover:border-[color:var(--gold)]"} p-5 group transition-colors block`}>
    <Icon size={20} className="text-[color:var(--gold)] mb-4" strokeWidth={1.5} />
    <p className="font-serif text-xl text-white group-hover:text-[color:var(--gold)] transition-colors">{label}</p>
    <p className="text-white/40 text-xs mt-1">{desc}</p>
    <ArrowRight size={12} className="text-white/30 mt-3 group-hover:text-[color:var(--gold)] group-hover:translate-x-1 transition-all" />
  </Link>
);

/**
 * SVG sparkline for the revenue series (no external chart lib).
 */
const RevenueSparkline = ({ series }) => {
  if (!series || series.length === 0) {
    return <p className="text-white/40 text-sm">Aucune donnée sur cette période.</p>;
  }
  const max = Math.max(1, ...series.map((s) => s.revenue));
  const W = 700, H = 180, P = 20;
  const step = (W - 2 * P) / Math.max(1, series.length - 1);
  const points = series.map((s, i) => {
    const x = P + i * step;
    const y = H - P - (s.revenue / max) * (H - 2 * P);
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `${P},${H - P} ${points} ${P + (series.length - 1) * step},${H - P}`;

  return (
    <div className="relative w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill="url(#sparkGrad)" />
        <polyline points={points} fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {series.map((s, i) => {
          const x = P + i * step;
          const y = H - P - (s.revenue / max) * (H - 2 * P);
          return s.revenue > 0 ? <circle key={i} cx={x} cy={y} r="2.5" fill="#D4AF37" /> : null;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-white/40 mt-2 label-caps tracking-[0.15em]">
        <span>{series[0]?.day}</span>
        <span>{series[series.length - 1]?.day}</span>
      </div>
    </div>
  );
};
